import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import type { LayoutConfig } from '@/utils/dealCalculations'
import {
  DEFAULT_BONUS_Y,
  DEFAULT_DESCRIPTION_Y,
  DEFAULT_LOYALTY_Y,
  DEFAULT_NEGOTIATION_Y,
  DEFAULT_PAYMENT_Y,
  DEFAULT_PRICE_Y,
  DEFAULT_PROJECT_TYPE_Y,
  DEFAULT_SUPPORT_HOURS_Y,
  DEFAULT_TITLE_Y,
} from '@/utils/dealCalculations'
import { snapDragPosition, snapDragY, uniqueTargets, type SnapGuide } from './layoutSnap'
import type { SlideCategoryBlock } from './computeSlideCategoryBlocks'

export function useSlidePreviewDrag(
  layout: LayoutConfig,
  onLayoutChange: (newLayout: LayoutConfig) => void,
  categoryBlocks: SlideCategoryBlock[],
  leftBlockWidth: number,
) {
  const [draggingKey, setDraggingKey] = useState<string | null>(null)
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([])

  const snapTargets = useMemo(() => {
    const col1 = layout.serviceBlockX || 38
    const col2 = layout.serviceBlockGap || 68
    const targetsX = uniqueTargets([
      leftBlockWidth,
      leftBlockWidth + 4,
      col1,
      col2,
      ...categoryBlocks.map((b) => b.x),
    ])
    const targetsY = uniqueTargets([
      layout.planTitleY ?? 2.7,
      layout.serviceBlockStartY ?? 10,
      layout.titleY ?? DEFAULT_TITLE_Y,
      layout.projectTypeY ?? DEFAULT_PROJECT_TYPE_Y,
      layout.descriptionY ?? DEFAULT_DESCRIPTION_Y,
      layout.supportHoursY ?? DEFAULT_SUPPORT_HOURS_Y,
      layout.bonusY ?? DEFAULT_BONUS_Y,
      layout.loyaltyY ?? DEFAULT_LOYALTY_Y,
      layout.negotiationY ?? DEFAULT_NEGOTIATION_Y,
      layout.priceY ?? DEFAULT_PRICE_Y,
      layout.paymentY ?? DEFAULT_PAYMENT_Y,
      ...categoryBlocks.map((b) => b.y),
    ])
    return { targetsX, targetsY, col1, col2 }
  }, [layout, categoryBlocks, leftBlockWidth])

  const getSnapTargetsExcluding = useCallback(
    (excludeKey: string | null) => {
      const excludeCat = excludeKey?.startsWith('category_') ? excludeKey.replace('category_', '') : null
      const targetsX = uniqueTargets(
        snapTargets.targetsX
          .filter((x) => {
            if (!excludeCat) return true
            const block = categoryBlocks.find((b) => b.categoria === excludeCat)
            return !block || Math.abs(block.x - x) > 0.05
          })
          .concat([snapTargets.col1, snapTargets.col2]),
      )
      const targetsY = uniqueTargets(
        snapTargets.targetsY.filter((y) => {
          if (!excludeCat) return true
          const block = categoryBlocks.find((b) => b.categoria === excludeCat)
          return !block || Math.abs(block.y - y) > 0.05
        }),
      )
      return { targetsX, targetsY }
    },
    [snapTargets, categoryBlocks],
  )

  const handleDragStart = useCallback((e: MouseEvent, key: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingKey(key)
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!draggingKey) return
      const rect = e.currentTarget.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 100
      const y = ((e.clientY - rect.top) / rect.height) * 100
      const newLayout = { ...layout }

      if (draggingKey === 'leftBlockWidth') {
        const snapped = snapDragPosition({
          x,
          y: 0,
          targetsX: uniqueTargets([35, 40, 45, leftBlockWidth]),
          targetsY: [],
          minX: 20,
          maxX: 50,
          minY: 0,
          maxY: 100,
        })
        setSnapGuides(snapped.guides.filter((g) => g.orientation === 'vertical'))
        newLayout.leftBlockWidth = snapped.x
        newLayout.dividerX = newLayout.leftBlockWidth
      } else if (
        [
          'title',
          'projectType',
          'description',
          'supportHours',
          'bonus',
          'price',
          'payment',
          'loyalty',
          'negotiation',
          'planTitle',
        ].includes(draggingKey)
      ) {
        const { targetsY } = getSnapTargetsExcluding(draggingKey)
        const maxY = draggingKey === 'title' ? 25 : 99
        const snapped = snapDragY({ y, targetsY, minY: 1, maxY })
        setSnapGuides(snapped.guides)
        const yKeyMap: Record<string, keyof LayoutConfig> = {
          title: 'titleY',
          projectType: 'projectTypeY',
          description: 'descriptionY',
          supportHours: 'supportHoursY',
          bonus: 'bonusY',
          price: 'priceY',
          payment: 'paymentY',
          loyalty: 'loyaltyY',
          negotiation: 'negotiationY',
          planTitle: 'planTitleY',
        }
        const yKey = yKeyMap[draggingKey]
        if (yKey) (newLayout as unknown as Record<string, number>)[yKey] = snapped.y
      } else if (draggingKey.startsWith('category_')) {
        const catName = draggingKey.replace('category_', '')
        const { targetsX, targetsY } = getSnapTargetsExcluding(draggingKey)
        const minX = (layout.leftBlockWidth || 35) + 2
        const snapped = snapDragPosition({
          x,
          y,
          targetsX,
          targetsY,
          minX,
          maxX: 95,
          minY: 5,
          maxY: 90,
        })
        setSnapGuides(snapped.guides)
        const newCategoryCoords = { ...(layout.categoryCoords || {}) }
        newCategoryCoords[catName] = { x: snapped.x, y: snapped.y }
        newLayout.categoryCoords = newCategoryCoords
      } else {
        setSnapGuides([])
      }

      onLayoutChange(newLayout)
    },
    [draggingKey, layout, leftBlockWidth, getSnapTargetsExcluding, onLayoutChange],
  )

  const handleMouseUpOrLeave = useCallback(() => {
    if (draggingKey) setDraggingKey(null)
    setSnapGuides([])
  }, [draggingKey])

  return {
    draggingKey,
    snapGuides,
    handleDragStart,
    handleMouseMove,
    handleMouseUpOrLeave,
  }
}
